import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from "@angular/router";
import { Subscription, firstValueFrom } from 'rxjs';
import { settledCache } from '../../shared/utils/cache-ready';

import { User } from '../../objects/user';
import { CreateEntity } from '../create-entity';
import { DatabaseAction } from '../../objects/interface-library';

// Material imports
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';

// Services
import { CommonSheetsService } from '../../service/google-sheets/common-sheets.service';
import { PermissionsService } from '../../service/permissions/permissions.service';
import { EntityFormDataService } from '../../service/form-data/entity-form-data.service';

@Component({
  selector: 'app-new-user-full-page',
  templateUrl: './new-user-full-page.component.html',
  styleUrls: ['./new-user-full-page.component.css'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatCardModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatProgressSpinner,
    MatOption,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule
  ],
  providers: [EntityFormDataService]
})
export class NewUserFullPageComponent extends CreateEntity implements OnInit, OnDestroy {
  // usersService and siteService are inherited from Entity base class as public properties
  private commonSheetsService = inject(CommonSheetsService);
  private permissionsService = inject(PermissionsService);
  private entityFormDataService = inject(EntityFormDataService);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  // Form management
  form: FormGroup;
  private formSubscription!: Subscription;

  currentUser: User = {} as User;
  originalUser: any = {};

  // UI State
  showAdditionalDetails = false;
  isEditMode = false;

  // Static field data
  allRoles: string[] = [];
  allDepartments: string[] = [];
  allSites: any[] = [];

  saving = false;
  loadingUserData = false;

  constructor(
    public dialog: MatDialog,
    public router: Router,
    private fb: FormBuilder
  ) {
    super();

    // Initialize the form with validation
    this.form = this.fb.group({
      // ID and metadata
      userNum: [''],
      userId: [''],

      // Name fields
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: ['', Validators.required],
      suffix: [''],
      fullName: [''],
      friendlyName: [''],
      userFullName: [''],

      // Contact information
      email: ['', [Validators.required, Validators.email]],
      officePhone: [''],
      mobilePhone: [''],
      faxPhone: [''],

      // User-specific fields
      role: [''],
      departments: [[]],
      sites: [[]],

      // Additional metadata
      dateAdded: [''],
      lastLogin: [''],
      lastLoginIp: [''],
      loginCount: [0],
      other: ['']
    });
  }

  async ngOnInit() {
    this.loadingUserData = true;

    try {
      const userNum = this.route.snapshot.paramMap.get('id');
      const navigation = this.router.getCurrentNavigation();
      const state = navigation?.extras?.state || history.state;

      // Load static field values
      await this.getStaticFieldValues();

      if (userNum) {
        // Load user from service using ID
        this.isEditMode = true;
        await this.loadUserByUserNum(userNum);
      } else if (state?.user) {
        // User passed via router state
        this.isEditMode = true;
        this.currentUser = state.user;
        this.originalUser = { ...state.user };
        this.populateFormWithUser(this.currentUser);
      } else {
        this.isEditMode = false;
        // Brand new user gets new userNum
        const timestamp = Date.now().toString();
        this.form.patchValue({
          userNum: timestamp,
          dateAdded: new Date().toISOString()
        });
      }

      // Subscribe to form changes
      this.formSubscription = this.form.valueChanges.subscribe(changes => {
        this.currentUser = { ...this.currentUser, ...changes };

        // Auto-generate userFullName when name fields change
        if (changes.firstName || changes.middleName || changes.lastName) {
          this.updateUserFullName();
        }
      });

    } finally {
      this.loadingUserData = false;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  async loadUserByUserNum(userId: string) {
    try {
      // Get user from service. settledCache, not firstValueFrom — if the cache
      // hasn't filled yet this find() misses and the caller reports the user as
      // not found, which is indistinguishable from a genuinely bad id.
      const users = await settledCache(this.usersService.allUsers$);
      // Try to find user by email (primary identifier), userId, or userNum
      const user = users.find((u: any) =>
        u.email === userId ||
        u.userId === userId ||
        u.userNum === userId
      );

      if (user) {
        //console.log('User found:', user);
        this.currentUser = user;
        this.originalUser = { ...user };
        this.populateFormWithUser(user);
      } else {
        console.error('User not found with identifier:', userId);
        alert('User not found');
        this.router.navigate(['/securehome/users']);
      }
    } catch (error) {
      console.error('Error loading user:', error);
      alert('Failed to load user');
    }
  }

  populateFormWithUser(user: User) {
    //console.log('Populating form with user:', user);

    this.form.patchValue({
      userNum: user.userNum,
      userId: user.userId,
      firstName: user.firstName,
      middleName: user.middleName,
      lastName: user.lastName,
      suffix: user.suffix,
      fullName: user.fullName,
      friendlyName: user.friendlyName,
      userFullName: user.userFullName,
      email: user.email,
      officePhone: user.officePhone,
      mobilePhone: user.mobilePhone,
      faxPhone: user.faxPhone,
      role: user.role,
      departments: Array.isArray(user.departments) ? user.departments : [],
      sites: Array.isArray(user.sites) ? user.sites : [],
      dateAdded: user.dateAdded,
      lastLogin: user.lastLogin,
      lastLoginIp: user.lastLoginIp,
      loginCount: user.loginCount || 0,
      other: user.other
    });
  }

  updateUserFullName() {
    const firstName = this.form.get('firstName')?.value || '';
    const middleName = this.form.get('middleName')?.value || '';
    const lastName = this.form.get('lastName')?.value || '';

    let userFullName = firstName;
    if (middleName) {
      userFullName += ' ' + middleName;
    }
    if (lastName) {
      userFullName += ' ' + lastName;
    }

    this.form.patchValue({
      userFullName: userFullName.trim(),
      fullName: userFullName.trim()
    }, { emitEvent: false });
  }

  async getStaticFieldValues() {
    try {
      // Load roles from EntityFormDataService
      this.allRoles = this.entityFormDataService.getUserRoles();

      // Load departments
      this.allDepartments = await firstValueFrom(
        this.commonSheetsService.getDepartments()
      );

      // Load sites
      this.allSites = await firstValueFrom(
        this.siteService.allSites$
      );

    } catch (error) {
      console.error('Error loading static field values:', error);
    }
  }

  // compareWith function for site selection
  compareSites(site1: any, site2: any): boolean {
    return site1 && site2 ? site1.siteNum === site2.siteNum : site1 === site2;
  }

  // Validation
  isFormValid(): boolean {
    const requiredFields = ['firstName', 'lastName', 'email'];
    return requiredFields.every(field => this.form.get(field)?.value);
  }

  getValidationMessage(): string {
    if (!this.form.get('firstName')?.value) return 'First name is required';
    if (!this.form.get('lastName')?.value) return 'Last name is required';
    if (!this.form.get('email')?.value) return 'Email is required';
    if (this.form.get('email')?.hasError('email')) return 'Valid email is required';
    return '';
  }

  toggleAdditionalDetails() {
    this.showAdditionalDetails = !this.showAdditionalDetails;
  }

  cancelSubmit() {
    this.router.navigateByUrl('/securehome/users');
  }

  async onSubmit() {
    if (!this.isFormValid()) {
      alert(this.getValidationMessage());
      return;
    }

    this.saving = true;
    const userToSave: User = this.form.value;

    // Ensure userNum exists
    if (!userToSave.userNum) {
      userToSave.userNum = Date.now().toString();
    }

    // Ensure userFullName is set
    if (!userToSave.userFullName) {
      this.updateUserFullName();
      userToSave.userFullName = this.form.get('userFullName')?.value;
    }

    // Set userId from email if not set
    if (!userToSave.userId && userToSave.email) {
      userToSave.userId = userToSave.email.toLowerCase();
    }

    // Ensure dateAdded is set for new users
    if (!this.isEditMode && !userToSave.dateAdded) {
      userToSave.dateAdded = new Date().toISOString();
    }

    //console.log('Saving user:', userToSave);

    try {
      const userId = userToSave.userId || userToSave.email!.toLowerCase();

      // Build permissions object from user data
      const permissions = {
        role: userToSave.role,
        departments: userToSave.departments,
        sites: userToSave.sites
      };

      // Use PermissionsService to update user permissions
      const permissionsResponse = await this.permissionsService.updateUserPermissions(
        userId,
        permissions
      );
      //console.log('User permissions saved successfully', permissionsResponse);

      // Build metadata object from user data
      const metadata = {
        firstName: userToSave.firstName,
        middleName: userToSave.middleName,
        lastName: userToSave.lastName,
        suffix: userToSave.suffix,
        fullName: userToSave.fullName,
        friendlyName: userToSave.friendlyName,
        userFullName: userToSave.userFullName,
        officePhone: userToSave.officePhone,
        mobilePhone: userToSave.mobilePhone,
        faxPhone: userToSave.faxPhone,
        other: userToSave.other
      };

      // Save user metadata (firstName, lastName, phone numbers, etc.)
      const metadataResponse = await firstValueFrom(
        this.usersService.updateUserMetadata(userId, metadata)
      );
      //console.log('User metadata saved successfully', metadataResponse);

      // Manually refresh the cache
      this.usersService.refreshAllUsersRecords();

      this.saving = false;

      // Navigate to user detail page using email (primary identifier)
      const navigateToId = userToSave.email || userToSave.userId || userToSave.userNum;
      this.router.navigate(['/securehome/user', navigateToId]);

    } catch (error) {
      console.error('Failed to save user:', error);
      this.saving = false;
      alert('Failed to save user. Please try again.');
    }
  }
}
